import { BitkitIconComponent } from '@bitrise/bitkit-v2';
import { Box, BoxProps } from '@chakra-ui/react/box';
import { Text } from '@chakra-ui/react/text';
import padStart from 'lodash/padStart';
import { ElementType } from 'react';

import { getDuration, useDuration } from '@/hooks/api/useDuration';

type ClockProps = {
  as?: ElementType;
  startDate: Date | number;
  endDate?: Date | number;
  icon?: BitkitIconComponent;
  iconSize?: string;
  colorScheme?: 'gray';
  alignment?: 'start' | 'end';
} & BoxProps;

/** A duration's segments as `Clock` shows them -- the one place the format lives. */
const durationSegments = (duration: ReturnType<typeof getDuration>): string[] => {
  if (duration.milliseconds) {
    return ['<1s'];
  }
  const hasMinutes = !!duration.minutes || !!duration.hours;
  return [
    duration.days ? `${duration.days}d` : '',
    duration.hours ? `${duration.hours}h` : '',
    hasMinutes ? `${padStart(duration.minutes?.toString(), duration.hours ? 2 : 1, '0')}m` : '',
    `${padStart(duration.seconds?.toString(), hasMinutes ? 2 : 1, '0')}s`,
  ].filter(Boolean);
};

const Clock = (props: ClockProps) => {
  const { as = 'div', startDate, endDate, icon: Icon, iconSize, colorScheme, alignment = 'start', ...rest } = props;
  const duration = useDuration(startDate, endDate);
  let color: 'color/neutral/strong' | undefined;
  if (colorScheme === 'gray') {
    color = 'color/neutral/strong';
  }

  return (
    <Box
      as={as as ElementType}
      display="flex"
      gap="4"
      alignItems="center"
      justifyContent={alignment === 'start' ? 'flex-start' : 'flex-end'}
      {...rest}
    >
      {Icon && <Icon width={iconSize} color={color ? 'icon/secondary' : undefined} height={iconSize} />}
      {durationSegments(duration).map((segment) => (
        <Text as="span" color={color} css={{ fontVariantNumeric: 'tabular-nums' }} key={segment}>
          {segment}
        </Text>
      ))}
    </Box>
  );
};

/** The text a finished `Clock` shows, for places that take plain text. */
export const formatClockDuration = (startDate: Date | number, endDate: Date | number): string =>
  durationSegments(getDuration(startDate, endDate)).join(' ');

export default Clock;
